import { useEffect, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { useDebounced } from "@/hooks/useFetch";
import { cn } from "@/lib/utils";

/** Search any variant (SKU / barcode / product / brand) and pick one. */
export const VariantPicker = ({ value, onSelect, placeholder = "Search SKU, product or brand…", disabled, testId = "variant-picker", exclude = [] }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 250);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    api.get("/admin/variants/search", { params: { q: dq, limit: 30 } })
      .then((r) => alive && setItems(r.data.items || []))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [dq, open]);

  const label = value ? `${value.sku} · ${value.product?.brand_name || ""} ${value.product?.name || ""} · ${value.color} / ${value.size}` : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} disabled={disabled} className="w-full justify-between font-normal h-10 rounded-lg" data-testid={`${testId}-trigger`}>
          <span className={cn("truncate text-left", !value && "text-muted-foreground")}>{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[360px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={q} onValueChange={setQ} placeholder="Type to search…" data-testid={`${testId}-input`} />
          <CommandList>
            <CommandEmpty>{loading ? "Searching…" : "No variants found."}</CommandEmpty>
            <CommandGroup>
              {items.filter((v) => !exclude.includes(v.id)).map((v) => (
                <CommandItem key={v.id} value={v.id} onSelect={() => { onSelect(v); setOpen(false); }} className="flex items-start gap-3 py-2" data-testid={`${testId}-option`}>
                  <Check className={cn("mt-1 h-4 w-4 shrink-0", value?.id === v.id ? "opacity-100" : "opacity-0")} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{v.product?.brand_name} <span className="font-normal text-foreground/80">{v.product?.name}</span></p>
                    <p className="text-xs text-muted-foreground tabular">{v.sku} · {v.color} / {v.size} · {money(v.selling_price)} · avail {v.stock?.available ?? 0}</p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
